import { useState, useEffect, useCallback } from "react";
import "../styles/SideMenu.css";
import { PLUS } from "../helpers/subscriptionTiers";
import dayjs from "dayjs";
import { FaLink } from "react-icons/fa";
import { FiCreditCard, FiSettings, FiUser, FiLogOut } from "react-icons/fi";
import { toast } from "react-toastify";
import { fontGroups } from "../fonts/fontGroups.js";
import {
  useMenu,
  useUser,
  useFilters,
  useUI,
  useAuthActions,
} from "../hooks/index.js";
import { baseBackendURL } from "../helpers/baseUrl.js";

const isProduction = process.env.NODE_ENV === "production";
const baseFrontendURL = isProduction
  ? "https://www.prioritypop.com"
  : "http://localhost:3000";

function debounce(func, wait) {
  let timeout;
  return (...args) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
}

const SideMenu = () => {
  const {
    menuOpen,
    showSubscriptionMenu,
    setShowSubscriptionMenu,
    setMenuOpen,
  } = useMenu();
  const {
    subscriptionTier,
    subscriptionEnd,
    email,
    isDemo,
    preferences,
    setPreferences,
    completedTasks,
    setCompletedTasks,
    referralCode,
  } = useUser();

  const loggedIn = !!email;
  const { activeSlate, slates } = useFilters();
  const { selectedFont, setSelectedFont } = useUI();
  const { logout } = useAuthActions();

  const [view, setView] = useState("normal");
  const [newCategoryName, setNewCategoryName] = useState("");
  const [newCategoryColor, setNewCategoryColor] = useState("");
  const [colorOptions] = useState([
    { name: "Default", hex: "#FF5733" },
    { name: "Pink", hex: "#FFC0CB" },
    { name: "Purple", hex: "#800080" },
    { name: "Red", hex: "#FF5733" },
    { name: "Orange", hex: "#FFA500" },
    { name: "Olive", hex: "#6B8E23" },
    { name: "Green", hex: "#33FF57" },
    { name: "Teal", hex: "#20C9A5" },
    { name: "Blue", hex: "#5733FF" },
    { name: "Yellow", hex: "#FFD700" },
  ]);

  const fetchCompletedTasks = useCallback(() => {
    fetch(`${baseBackendURL}/tasks-completed?slate=${slates[activeSlate]}`, {
      credentials: "include",
    })
      .then((res) => res.json())
      .then((data) => setCompletedTasks(data))
      .catch((err) => console.error("Error fetching completed tasks:", err));
  }, [activeSlate, slates, setCompletedTasks]);

  const fetchPreferences = useCallback(() => {
    fetch(`${baseBackendURL}/preferences?slate=${slates[activeSlate]}`, {
      credentials: "include",
    })
      .then((res) => res.json())
      .then((data) => setPreferences(data))
      .catch((err) => console.error("Error fetching preferences:", err));
  }, [activeSlate, slates, setPreferences]);

  useEffect(() => {
    fetchCompletedTasks();
    fetchPreferences();
  }, [fetchCompletedTasks, fetchPreferences]);

  const saveFontPreference = async (selectedFont) => {
    try {
      const response = await fetch(`${baseBackendURL}/preferences/font`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          font: selectedFont,
          slate: slates[activeSlate],
        }),
      });

      const data = await response.json();
      if (!data.success) {
        console.error("Error saving font:", data.message);
      }
    } catch (error) {
      console.error("Failed to save font preference:", error);
    }
  };

  const savePreference = useCallback(
    debounce((category, slate, name, color, hex) => {
      fetch(`${baseBackendURL}/preferences`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ category, name, color, hex, slate }),
      })
        .then((res) => res.json())
        .then((data) => setPreferences(data))
        .catch((err) => console.error("Error saving preference:", err));
    }, 500),
    [setPreferences]
  );

  const handleCategoryChange = (category, field, value) => {
    setPreferences((prev) =>
      prev.map((pref) =>
        pref.category === category && pref.slate === slates[activeSlate]
          ? { ...pref, [field]: value }
          : pref
      )
    );

    // Trigger save after a delay
    savePreference(
      category,
      slates[activeSlate],
      field === "name"
        ? value
        : preferences.find((p) => p.category === category)?.name,
      field === "color"
        ? value
        : preferences.find((p) => p.category === category)?.color,
      field === "color"
        ? colorOptions.find((opt) => opt.name === value)?.hex || ""
        : preferences.find((p) => p.category === category)?.hex
    );
  };

  const handleCategoryBlur = (category) => {
    const pref = preferences.find(
      (p) => p.category === category && p.slate === slates[activeSlate]
    );
    if (pref) {
      // Save immediately on blur
      savePreference(
        category,
        pref.slate,
        pref.name,
        pref.color,
        colorOptions.find((opt) => opt.name === pref.color)?.hex || ""
      );
    }
  };

  const handleLogoutClick = useCallback(async () => {
    try {
      await logout();
    } catch (error) {
      console.error("Error during logout:", error);
    }
  }, [logout]);

  const copyReferralCodeToClipboard = () => {
    if (referralCode) {
      navigator.clipboard
        .writeText(`${baseFrontendURL}?ref=${referralCode}`)
        .then(() => {
          toast.success("Referral link copied to clipboard!");
        });
    }
  };

  return (
    <nav
      className={`side-menu ${menuOpen ? "open" : ""}`}
      style={{
        paddingTop: loggedIn ? "0.2rem" : "",
      }}
    >
      {view === "normal" && (
        <>
          <div className="completed-tasks">
            <h3>Completed Tasks</h3>
            <p>Slate: {slates[activeSlate]}</p>
            <div>
              <p>This week: {completedTasks.this_week}</p>
              <p>Last 30 days: {completedTasks.last_30_days}</p>
              <p>Lifetime: {completedTasks.lifetime}</p>
            </div>
          </div>

          <div className="completed-tasks orange-box">
            <h3>Subscription</h3>
            <div>
              <div className="subscription-content">
                Tier:{" "}
                <b>
                  {isDemo
                    ? "Demo"
                    : mapSubscriptionTierToString(subscriptionTier)}
                </b>
              </div>

              {subscriptionTier > 0 && subscriptionEnd && (
                <div className="subscription-content">
                  Expires: <b> {dayjs(subscriptionEnd).format("MM/DD/YY")}</b>
                </div>
              )}

              {!isDemo && (
                <>
                  <br />
                  <div className="subscription-cta">
                    Give 3 months and get 3 months of Pro if a friend signs up
                    and purchases PriorityPop with your <br />
                    <span
                      onClick={copyReferralCodeToClipboard}
                      style={{
                        cursor: "pointer",
                        color: "#0078d7",
                        textDecoration: "underline",
                      }}
                    >
                      <FaLink /> referral link
                    </span>
                    !
                  </div>
                </>
              )}
            </div>
          </div>

          <div className="nav-buttons">
            <button
              onClick={() => {
                if (!showSubscriptionMenu) {
                  setShowSubscriptionMenu(true);
                  setMenuOpen(false); // Close the sidebar
                } else {
                  setShowSubscriptionMenu(false);
                  setView("normal");
                }
              }}
            >
              <FiCreditCard /> Subscription
            </button>
            <button onClick={() => setView("preferences")}>
              <FiSettings /> Preferences
            </button>
            <button onClick={() => setView("account")}>
              <FiUser /> Account
            </button>
            <button onClick={handleLogoutClick}>
              <FiLogOut /> Log Out
            </button>
          </div>
        </>
      )}
      {view === "preferences" && (
        <>
          <button className="back-button" onClick={() => setView("normal")}>
            Back
          </button>
          <h3>Bubble Font</h3>
          <div className="font-preference">
            <label>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "14px",
                }}
              >
                <select
                  value={selectedFont}
                  onChange={(e) => {
                    const selectedFont = e.target.value;
                    setSelectedFont(selectedFont);
                    localStorage.setItem("selectedFont", selectedFont);
                    saveFontPreference(selectedFont);
                  }}
                >
                  {fontGroups.map((group) => (
                    <optgroup key={group.label} label={group.label}>
                      {group.fonts.map((font) => (
                        <option
                          key={font.value}
                          value={font.value}
                          style={{ fontFamily: font.value }}
                        >
                          {font.name}
                        </option>
                      ))}
                    </optgroup>
                  ))}
                </select>
              </div>
            </label>
          </div>
          {subscriptionTier >= PLUS ? (
            preferences.filter(
              (pref) =>
                pref.slate === slates[activeSlate] && pref.category !== 0 // exclude font preference row
            ).length < 10 && (
              <>
                <h3 style={{ textAlign: "center", marginTop: "0px" }}>
                  Categories
                </h3>
                <div className="new-category">
                  <label>
                    Name:
                    <input
                      type="text"
                      placeholder="New Category"
                      value={newCategoryName}
                      onChange={(e) => setNewCategoryName(e.target.value)}
                    />
                  </label>
                  <label style={{ marginBottom: "1em" }}>
                    Color:
                    <select
                      value={newCategoryColor}
                      onChange={(e) => setNewCategoryColor(e.target.value)}
                    >
                      <option value="">Select Color</option>
                      {colorOptions.map((option) => (
                        <option key={option.name} value={option.name}>
                          {option.name}
                        </option>
                      ))}
                    </select>
                  </label>
                  <button
                    className="button"
                    onClick={() => {
                      const selected = colorOptions.find(
                        (c) => c.name === newCategoryColor
                      );
                      savePreference(
                        preferences.filter(
                          (pref) => pref.slate === slates[activeSlate]
                        ).length + 1,
                        slates[activeSlate],
                        newCategoryName,
                        selected?.name || "",
                        selected?.hex || ""
                      );
                      setNewCategoryName("");
                      setNewCategoryColor("");
                    }}
                  >
                    Add Category
                  </button>
                </div>
              </>
            )
          ) : (
            <div className="upgrade-prompt-section">
              <h3 style={{ textAlign: "center", marginTop: "0px" }}>
                Categories
              </h3>
              <div
                className="upgrade-prompt-box"
                onClick={() => setShowSubscriptionMenu(true)}
              >
                <p className="upgrade-paragraph">
                  Subscribe to unlock custom categories and organize your tasks
                  with colors!
                </p>
                <button className="upgrade-button">Subscribe</button>
              </div>
            </div>
          )}

          {preferences.filter(
            (pref) => pref.slate === slates[activeSlate] && pref.category !== 0 // exclude font preference row
          ).length && <div className="secondary-color">Edit Categories</div>}
          <div className="preferences-list">
            {preferences
              .filter(
                (pref) =>
                  pref.slate === slates[activeSlate] && pref.category !== 0 // exclude font preference row
              ) // Filter by active slate & exclude font preference row
              .map((pref) => (
                <>
                  <div
                    key={`${pref.category}-${pref.slate}`}
                    className="preference-item"
                  >
                    <label>
                      C{pref.category}:
                      <input
                        type="text"
                        value={pref.name || ""}
                        onChange={(e) =>
                          handleCategoryChange(
                            pref.category,
                            "name",
                            e.target.value
                          )
                        }
                        onBlur={() => handleCategoryBlur(pref.category)}
                      />
                    </label>
                    <label>
                      Color:
                      <select
                        value={pref.color || ""}
                        onChange={(e) =>
                          handleCategoryChange(
                            pref.category,
                            "color",
                            e.target.value
                          )
                        }
                        onBlur={() => handleCategoryBlur(pref.category)}
                      >
                        <option value="">Select Color</option>
                        {colorOptions.map((option) => (
                          <option key={option.name} value={option.name}>
                            {option.name}
                          </option>
                        ))}
                      </select>
                    </label>
                  </div>
                </>
              ))}
          </div>
        </>
      )}
      {view === "account" && (
        <>
          <button className="back-button" onClick={() => setView("normal")}>
            Back
          </button>
          <div className="account-view">
            <h2 className="account-title">Account Info</h2>

            <div className="account-section">
              <label className="account-label">Email</label>
              <div className="account-email-box">{email}</div>
            </div>

            <div className="account-section">
              <label className="account-label">Subscription Tier</label>
              <div className="account-subscription-box">
                {mapSubscriptionTierToString(subscriptionTier)}
              </div>
            </div>

            {subscriptionTier > 0 && subscriptionEnd && (
              <div className="account-section">
                <label className="account-label">Subscription Expires</label>
                <div className="account-expires-box">
                  {dayjs(subscriptionEnd).format("MM/DD/YYYY")}
                </div>
              </div>
            )}
          </div>
        </>
      )}
    </nav>
  );
};

const mapSubscriptionTierToString = (subscriptionTier) => {
  switch (subscriptionTier) {
    case 0:
      return "Personal";
    case 1:
      return "Plus";
    case 2:
      return "Pro";
    default:
      return "Unknown";
  }
};

export default SideMenu;
